import React, { useState } from 'react';
import {
  useListHolidaysQuery,
  useCreateHolidayMutation,
  useUpdateHolidayMutation,
  useDeleteHolidayMutation,
  useListSummerSchedulesQuery,
  useCreateSummerScheduleMutation,
  useUpdateSummerScheduleMutation,
  useDeleteSummerScheduleMutation,
} from '../app/api';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
};

const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' },
];

const sectionCard = (title, icon, children) => (
  <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(10,41,71,0.1)', overflow: 'hidden', marginBottom: '1.5rem' }}>
    <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <i className={`bi bi-${icon}`} style={{ color: brand.goldenrod }} />
      <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>{title}</h5>
    </div>
    <div style={{ padding: '1.25rem' }}>{children}</div>
  </div>
);

const inputStyle = {
  padding: '8px 12px', border: `1px solid ${brand.softGray}`,
  borderRadius: '6px', fontSize: '0.875rem', color: brand.deepBlue, outline: 'none',
  fontFamily: 'inherit',
};
const btnPrimary = {
  background: brand.deepBlue, color: 'white', border: 'none', borderRadius: '6px',
  padding: '8px 16px', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', letterSpacing: '0.5px',
};
const btnOutline = {
  background: 'transparent', color: brand.tealBlue, border: `1px solid ${brand.tealBlue}`,
  borderRadius: '6px', padding: '6px 14px', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
};
const btnDanger = {
  background: 'transparent', color: brand.charcoal, border: `1px solid ${brand.charcoal}`,
  borderRadius: '6px', padding: '6px 14px', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
};
const fieldLabel = { display: 'flex', flexDirection: 'column', gap: '3px', fontSize: '0.75rem', color: brand.charcoal, fontWeight: 600 };

const toDateInput = (iso) => (iso ? iso.slice(0, 10) : '');
const fmtDate = (iso) => (iso ? new Date(iso.slice(0, 10) + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const fmtTime = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
};

const emptyHoliday = { name: '', holiday_date: '', end_date: '' };
const emptySummer = { label: '', start_date: '', end_date: '', weekday: 4, end_time: '15:00' };

export default function HRHolidays() {
  const { data: holidays = [], isLoading: hLoading } = useListHolidaysQuery({});
  const { data: summer = [], isLoading: sLoading } = useListSummerSchedulesQuery({});

  const [createHoliday, hCreate] = useCreateHolidayMutation();
  const [updateHoliday, hUpdate] = useUpdateHolidayMutation();
  const [deleteHoliday] = useDeleteHolidayMutation();
  const [createSummer, sCreate] = useCreateSummerScheduleMutation();
  const [updateSummer, sUpdate] = useUpdateSummerScheduleMutation();
  const [deleteSummer] = useDeleteSummerScheduleMutation();

  // ── Holiday form state ──
  const [showHolidayForm, setShowHolidayForm] = useState(false);
  const [holidayDraft, setHolidayDraft] = useState(emptyHoliday);
  const [editHolidayId, setEditHolidayId] = useState(null);

  // ── Summer form state ──
  const [showSummerForm, setShowSummerForm] = useState(false);
  const [summerDraft, setSummerDraft] = useState(emptySummer);
  const [editSummerId, setEditSummerId] = useState(null);

  const holidaySaving = hCreate.isLoading || hUpdate.isLoading;
  const summerSaving = sCreate.isLoading || sUpdate.isLoading;

  // ── Holiday handlers ──
  const startNewHoliday = () => { setEditHolidayId(null); setHolidayDraft(emptyHoliday); setShowHolidayForm(true); };
  const startEditHoliday = (h) => {
    setEditHolidayId(h.id);
    setHolidayDraft({ name: h.name, holiday_date: toDateInput(h.holiday_date), end_date: toDateInput(h.end_date) });
    setShowHolidayForm(true);
  };
  const submitHoliday = async () => {
    if (!holidayDraft.name.trim() || !holidayDraft.holiday_date) return;
    const payload = {
      name: holidayDraft.name.trim(),
      holiday_date: holidayDraft.holiday_date,
      end_date: holidayDraft.end_date || null,
    };
    if (editHolidayId) await updateHoliday({ id: editHolidayId, ...payload }).unwrap();
    else await createHoliday(payload).unwrap();
    setShowHolidayForm(false);
    setHolidayDraft(emptyHoliday);
    setEditHolidayId(null);
  };
  const removeHoliday = async (id) => {
    if (!window.confirm('Delete this holiday?')) return;
    await deleteHoliday({ id }).unwrap();
    if (editHolidayId === id) { setShowHolidayForm(false); setEditHolidayId(null); }
  };

  // ── Summer handlers ──
  const startNewSummer = () => { setEditSummerId(null); setSummerDraft(emptySummer); setShowSummerForm(true); };
  const startEditSummer = (s) => {
    setEditSummerId(s.id);
    setSummerDraft({
      label: s.label || '',
      start_date: toDateInput(s.start_date),
      end_date: toDateInput(s.end_date),
      weekday: s.weekday,
      end_time: (s.end_time || '15:00').slice(0, 5),
    });
    setShowSummerForm(true);
  };
  const submitSummer = async () => {
    if (!summerDraft.start_date || !summerDraft.end_date || !summerDraft.end_time) return;
    const payload = {
      label: summerDraft.label.trim() || null,
      start_date: summerDraft.start_date,
      end_date: summerDraft.end_date,
      weekday: Number(summerDraft.weekday),
      end_time: summerDraft.end_time,
    };
    if (editSummerId) await updateSummer({ id: editSummerId, ...payload }).unwrap();
    else await createSummer(payload).unwrap();
    setShowSummerForm(false);
    setSummerDraft(emptySummer);
    setEditSummerId(null);
  };
  const removeSummer = async (id) => {
    if (!window.confirm('Delete this summer schedule?')) return;
    await deleteSummer({ id }).unwrap();
    if (editSummerId === id) { setShowSummerForm(false); setEditSummerId(null); }
  };

  const weekdayLabel = (w) => (WEEKDAY_OPTIONS.find(o => o.value === w) || {}).label || '';

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: '1.5rem' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h4 style={{ color: brand.deepBlue, fontWeight: 800, margin: 0 }}>Holidays &amp; Summer Hours</h4>
        <p style={{ color: brand.charcoal, margin: 0, fontSize: '0.875rem' }}>Manage the company holidays and summer short work days shown on the employee Holidays calendar</p>
      </div>

      {/* ── Company Holidays ── */}
      {sectionCard('Company Holidays', 'calendar-event',
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
            <button style={btnPrimary} onClick={() => (showHolidayForm && !editHolidayId ? setShowHolidayForm(false) : startNewHoliday())} disabled={holidaySaving}>
              <i className="bi bi-plus me-1" />{showHolidayForm && !editHolidayId ? 'Cancel' : 'New Holiday'}
            </button>
          </div>

          {showHolidayForm && (
            <div style={{ background: brand.softGray, borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-end' }}>
                <label style={{ ...fieldLabel, flex: '2 1 220px' }}>
                  Holiday name
                  <input style={inputStyle} placeholder="e.g. Independence Day"
                    value={holidayDraft.name} onChange={e => setHolidayDraft(p => ({ ...p, name: e.target.value }))} />
                </label>
                <label style={fieldLabel}>
                  Date
                  <input type="date" style={inputStyle}
                    value={holidayDraft.holiday_date} onChange={e => setHolidayDraft(p => ({ ...p, holiday_date: e.target.value }))} />
                </label>
                <label style={fieldLabel}>
                  End date (optional)
                  <input type="date" style={inputStyle}
                    value={holidayDraft.end_date} onChange={e => setHolidayDraft(p => ({ ...p, end_date: e.target.value }))} />
                </label>
                <button style={btnPrimary} onClick={submitHoliday} disabled={holidaySaving || !holidayDraft.name.trim() || !holidayDraft.holiday_date}>
                  {holidaySaving ? 'Saving…' : editHolidayId ? 'Save' : 'Add'}
                </button>
              </div>
              <div style={{ fontSize: '0.7rem', color: brand.charcoal, marginTop: '0.5rem' }}>
                <i className="bi bi-info-circle me-1" />Set an end date only for multi-day closures (e.g. a week-long break). Leave it blank for a single day.
              </div>
            </div>
          )}

          {hLoading && <p style={{ color: brand.charcoal, margin: 0 }}>Loading…</p>}
          {!hLoading && holidays.length === 0 && <p style={{ color: brand.charcoal, margin: 0 }}>No holidays yet.</p>}

          {holidays.map(h => (
            <div key={h.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', borderBottom: `1px solid ${brand.softGray}`, padding: '0.7rem 0' }}>
              <div>
                <span style={{ fontWeight: 700, color: brand.deepBlue }}>{h.name}</span>
                <span style={{ marginLeft: '0.75rem', fontSize: '0.8rem', color: brand.charcoal }}>
                  {fmtDate(h.holiday_date)}{h.end_date ? ` – ${fmtDate(h.end_date)}` : ''}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                <button style={btnOutline} onClick={() => startEditHoliday(h)}>Edit</button>
                <button style={btnDanger} onClick={() => removeHoliday(h.id)}>Delete</button>
              </div>
            </div>
          ))}
        </>
      )}

      {/* ── Summer Short Work Days ── */}
      {sectionCard('Summer Short Work Days', 'sun',
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
            <button style={btnPrimary} onClick={() => (showSummerForm && !editSummerId ? setShowSummerForm(false) : startNewSummer())} disabled={summerSaving}>
              <i className="bi bi-plus me-1" />{showSummerForm && !editSummerId ? 'Cancel' : 'New Summer Schedule'}
            </button>
          </div>

          {showSummerForm && (
            <div style={{ background: brand.softGray, borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-end' }}>
                <label style={{ ...fieldLabel, flex: '2 1 200px' }}>
                  Label (optional)
                  <input style={inputStyle} placeholder="e.g. Summer Hours"
                    value={summerDraft.label} onChange={e => setSummerDraft(p => ({ ...p, label: e.target.value }))} />
                </label>
                <label style={fieldLabel}>
                  Day of week
                  <select style={inputStyle} value={summerDraft.weekday}
                    onChange={e => setSummerDraft(p => ({ ...p, weekday: Number(e.target.value) }))}>
                    {WEEKDAY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </label>
                <label style={fieldLabel}>
                  Office closes at
                  <input type="time" style={inputStyle}
                    value={summerDraft.end_time} onChange={e => setSummerDraft(p => ({ ...p, end_time: e.target.value }))} />
                </label>
                <label style={fieldLabel}>
                  From
                  <input type="date" style={inputStyle}
                    value={summerDraft.start_date} onChange={e => setSummerDraft(p => ({ ...p, start_date: e.target.value }))} />
                </label>
                <label style={fieldLabel}>
                  To
                  <input type="date" style={inputStyle}
                    value={summerDraft.end_date} onChange={e => setSummerDraft(p => ({ ...p, end_date: e.target.value }))} />
                </label>
                <button style={btnPrimary} onClick={submitSummer} disabled={summerSaving || !summerDraft.start_date || !summerDraft.end_date || !summerDraft.end_time}>
                  {summerSaving ? 'Saving…' : editSummerId ? 'Save' : 'Add'}
                </button>
              </div>
              <div style={{ fontSize: '0.7rem', color: brand.charcoal, marginTop: '0.5rem' }}>
                <i className="bi bi-info-circle me-1" />Every {weekdayLabel(Number(summerDraft.weekday))} between these dates the office closes early at {fmtTime(summerDraft.end_time)}.
              </div>
            </div>
          )}

          {sLoading && <p style={{ color: brand.charcoal, margin: 0 }}>Loading…</p>}
          {!sLoading && summer.length === 0 && <p style={{ color: brand.charcoal, margin: 0 }}>No summer schedules yet.</p>}

          {summer.map(s => (
            <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', borderBottom: `1px solid ${brand.softGray}`, padding: '0.7rem 0' }}>
              <div>
                <span style={{ fontWeight: 700, color: brand.deepBlue }}>
                  {s.label ? `${s.label} — ` : ''}{weekdayLabel(s.weekday)}s, closes {fmtTime(s.end_time)}
                </span>
                <span style={{ marginLeft: '0.75rem', fontSize: '0.8rem', color: brand.charcoal }}>
                  {fmtDate(s.start_date)} – {fmtDate(s.end_date)}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                <button style={btnOutline} onClick={() => startEditSummer(s)}>Edit</button>
                <button style={btnDanger} onClick={() => removeSummer(s.id)}>Delete</button>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
